import React, { useMemo } from 'react';
import { LuPercent, LuCircleX, LuPlay, LuBot, LuShuffle, LuTimer, LuInfo } from 'react-icons/lu';
import { MetricCard } from '../../ui';
import { SectionError } from './SectionCard';
import {
  RANGE_LABEL,
  bucketLabel,
  comparisonText,
  fmtCount,
  fmtDuration,
  fmtPct,
  isNum,
} from './format';

function InfoTip({ text }) {
  if (!text) return null;
  return (
    <span
      className="pqd-info"
      tabIndex={0}
      role="note"
      aria-label={text}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <LuInfo size={12} aria-hidden="true" />
      <span className="pqd-info__bubble" aria-hidden="true">{text}</span>
    </span>
  );
}

function series(buckets, granularity, pick, format) {
  const points = (buckets || []).filter((b) => isNum(pick(b)));
  if (points.length < 2) return undefined;
  return {
    data: points.map(pick),
    labels: points.map((b) => bucketLabel(b.key, granularity)),
    format,
  };
}

function KpiSkeleton() {
  return (
    <div className="rt-metric-card pqd-kpi-skeleton" aria-hidden="true">
      <div className="rt-skeleton" style={{ height: 12, width: '50%' }} />
      <div className="rt-skeleton" style={{ height: 26, width: '40%', marginTop: 12 }} />
      <div className="rt-skeleton" style={{ height: 10, width: '70%', marginTop: 10 }} />
    </div>
  );
}

export default function KpiStrip({ summary, loading, error, onRetry, range, onOpenFailures, navigateTo }) {
  const kpis = summary?.kpis;
  const defs = summary?.definitions || {};
  const trends = summary?.trends;
  const vs = `vs ${RANGE_LABEL[range] || 'previous period'}`;

  const sparks = useMemo(() => {
    const buckets = trends?.buckets || [];
    const g = trends?.granularity;
    return {
      passRate: series(buckets, g, (b) => b.pass_rate, fmtPct),
      executed: series(buckets, g, (b) => (b.results ? b.results : null), fmtCount),
      duration: series(buckets, g, (b) => b.avg_run_duration, fmtDuration),
    };
  }, [trends]);

  if (loading) {
    return (
      <section className="pqd-kpis" aria-label="Key quality metrics" aria-busy="true">
        {Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)}
      </section>
    );
  }
  if (error) {
    return <SectionError message={error} onRetry={onRetry} />;
  }
  if (!kpis) return null;

  const label = (text, def) => (
    <span className="pqd-kpi-label">
      {text}
      <InfoTip text={def} />
    </span>
  );
  const context = (kpi, fallback) => {
    if (kpi?.comparable && kpi.delta === 0) return `No change ${vs}`;
    if (kpi?.comparable) return vs;
    return fallback;
  };
  const trend = (kpi, unit) => (kpi?.comparable && kpi.delta !== 0 ? comparisonText(kpi, { unit }) : undefined);

  const pr = kpis.pass_rate;
  const ft = kpis.failing_tests;
  const te = kpis.tests_executed;
  const ac = kpis.automation_coverage;
  const fl = kpis.flaky_tests;
  const rd = kpis.avg_run_duration;

  return (
    <section className="pqd-kpis" aria-label="Key quality metrics">
      <MetricCard
        label={label('Latest pass rate', defs.pass_rate)}
        value={fmtPct(pr?.current)}
        icon={<LuPercent size={14} />}
        tone="success"
        trend={trend(pr, 'pct')}
        context={context(pr, isNum(pr?.current) ? `${fmtCount(pr.evaluated)} tests evaluated` : 'No pass/fail results')}
        sparkline={sparks.passRate}
        onClick={() => onOpenFailures('failing')}
      />
      <MetricCard
        label={label('Failing tests', defs.failing_tests)}
        value={fmtCount(ft?.current)}
        icon={<LuCircleX size={14} />}
        tone="danger"
        trendInvert
        trend={trend(ft, 'count')}
        context={context(ft, isNum(ft?.current) ? 'latest result failed' : 'No results in period')}
        onClick={() => onOpenFailures('failing')}
      />
      <MetricCard
        label={label('Tests executed', defs.tests_executed)}
        value={fmtCount(te?.current)}
        icon={<LuPlay size={14} />}
        tone="primary"
        trend={trend(te, 'count')}
        context={`${fmtCount(te?.results)} results${te?.comparable ? ` · ${vs}` : ''}`}
        sparkline={sparks.executed}
        onClick={() => navigateTo('executions')}
      />
      <MetricCard
        label={label('Automation coverage', defs.automation_coverage)}
        value={fmtPct(ac?.current)}
        icon={<LuBot size={14} />}
        tone="accent"
        context={isNum(ac?.current) ? `${fmtCount(ac.automated)} of ${fmtCount(ac.eligible)} active tests` : 'No active test cases'}
        onClick={() => navigateTo('test-cases?type=automated')}
      />
      <MetricCard
        label={label('Flaky tests', defs.flaky_tests)}
        value={fmtCount(fl?.current)}
        icon={<LuShuffle size={14} />}
        tone="warning"
        context={`last ${fl?.window_days ?? 30} days`}
        onClick={() => onOpenFailures('flaky')}
      />
      <MetricCard
        label={label('Avg run duration', defs.avg_run_duration)}
        value={fmtDuration(rd?.current)}
        icon={<LuTimer size={14} />}
        tone="info"
        trendInvert
        trend={trend(rd, 'seconds')}
        context={context(rd, isNum(rd?.current) ? `${fmtCount(rd.finished_runs)} finished runs` : 'No finished runs')}
        sparkline={sparks.duration}
        onClick={() => navigateTo('executions')}
      />
    </section>
  );
}
